/**
 * CustomerDogsPage — `/dashboard/customer/dogs`.
 *
 * Phase 2 extraction from `CustomerDashboard.jsx`'s Dogs section.
 * Reads + mutates `useAuth().profile.dogs` (same coupling the
 * original page used), so a customer who adds / edits / deletes a
 * dog here immediately sees the change in the overview's profile
 * shell too. Preserves prior testids (`dogs-section`,
 * `add-dog-button`, `dogs-empty-state`).
 */
import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PawPrint, Plus } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DogCard from "@/components/DogCard";
import ImpersonationConfirmGate, {
  useImpersonationGate,
} from "@/components/ImpersonationConfirmGate";

export default function CustomerDogsPage() {
  const { profile, setProfile } = useAuth();
  const { requestAction, gateProps } = useImpersonationGate();

  const handleAddDog = () => {
    requestAction("add a dog", async () => {
      try {
        const { data } = await api.post("/profiles/customer/dogs", {
          name: "New dog",
          during_cleaning: "inside_home",
        });
        const next = {
          ...profile,
          dogs: [...(profile?.dogs || []), data],
        };
        setProfile(next);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    });
  };

  const updateDog = (updated) => {
    const dogs = (profile?.dogs || []).map((d) =>
      d.id === updated.id ? { ...d, ...updated } : d
    );
    setProfile({ ...profile, dogs });
  };

  const removeDogLocal = (id) => {
    setProfile({ ...profile, dogs: (profile?.dogs || []).filter((d) => d.id !== id) });
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-10" data-testid="customer-dogs-page">
      <header className="mb-8">
        <span className="text-sm font-bold uppercase tracking-wider text-brand-orange">
          Customer dashboard
        </span>
        <h1 className="font-heading text-4xl font-bold text-pp-text sm:text-5xl">
          My dogs
        </h1>
        <p className="mt-2 text-muted-foreground">
          Add one or more dogs so your PooPartner knows what to expect.
        </p>
      </header>

      <Card
        className="rounded-3xl border-border bg-pp-surface p-8 shadow-none"
        data-testid="dogs-section"
      >
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-pp-text">
              Your pack
            </h2>
            <p className="text-sm text-muted-foreground">
              Each dog&apos;s notes flow through to the booking summary your PooPartner sees.
            </p>
          </div>
          <Button
            type="button"
            onClick={handleAddDog}
            className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
            data-testid="add-dog-button"
          >
            <Plus className="mr-1 h-4 w-4" />
            Add a dog
          </Button>
        </div>

        {(profile?.dogs || []).length === 0 ? (
          <div
            className="rounded-2xl border border-dashed border-border bg-muted/40 p-10 text-center"
            data-testid="dogs-empty-state"
          >
            <PawPrint className="mx-auto h-10 w-10 text-brand-orange" />
            <p className="mt-3 text-sm text-muted-foreground">
              No dogs added yet. Click &quot;Add a dog&quot; to introduce your pack.
            </p>
          </div>
        ) : (
          <div className="grid gap-5 lg:grid-cols-2">
            {(profile?.dogs || []).map((dog) => (
              <DogCard
                key={dog.id}
                dog={dog}
                onUpdate={updateDog}
                onDelete={removeDogLocal}
              />
            ))}
          </div>
        )}
      </Card>
      <ImpersonationConfirmGate {...gateProps} />
    </div>
  );
}
